import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Text } from '@uhg-abyss/web/ui/Text';
import { Fragment, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeFuncErrorEvent } from '../../common/AdobeTagging/adobeFuncErrorEvent';
import { PortalIdentifier } from '../../common/Portal';
import { PSXHeader } from '../../common/PSXHeader';
import { StillNeedHelp } from '../../common/StillNeedHelp';
import { PortalContext } from '../../context/PortalContext';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { isGuestFlow } from '../../utils/error.utils';
import { shouldUseGehaV2Styling } from '../../utils/gehaThemeUtils';
import { Icon } from './Icon';
import { IconContainer, PageContainer, PageContent } from './Page408.style';

export const Page408 = () => {
  const { t } = useTranslation();
  const refreshPage = () => {
    window.location.reload();
  };
  const breadcrumbs = [{ title: t('ERROR_PAGE.408.title'), href: '' }];
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    is404: true,
    pageName: 'error 408',
  });

  const { getRouteParams } = useRouter();
  const { token } = getRouteParams();
  const isGuest = isGuestFlow();
  const tokenData = tokenizer.parse(token) || {};
  const { message, code, source, uiSection } = tokenData;

  const { portalData } = useContext(PortalContext);
  const isGeha = portalData?.portalIdentifier === PortalIdentifier.GEHA;
  const isGEHAV2 = shouldUseGehaV2Styling();
  const isGehaTheme = isGEHAV2 || isGeha;

  useEffect(() => {
    adobeFuncErrorEvent({
      message,
      source,
      code: code ?? '408',
      uiSection,
    });
    adobePageTrackEvent();
  }, []);

  return (
    <Fragment>
      <PSXHeader
        breadcrumbs={breadcrumbs}
        dataTestId="error-408-search-form"
        showChips={false}
        showChoosePCPHeader={false}
        showDemoHeader={false}
        showMemberSelection={false}
      />
      <PageContainer>
        {!isGehaTheme && (
          <IconContainer>
            <Icon />
          </IconContainer>
        )}
        <PageContent>
          <Text
            color="#C40000"
            css={{
              'abyss-text-root': {
                marginTop: isGehaTheme ? '130px' : '40px',
                marginBottom: '4px',
                '@media (max-width: 464px)': {
                  marginBottom: '29px',
                  marginTop: 'unset',
                },
              },
            }}
            fontSize="xs"
            fontWeight="bold"
          >
            {t('ERROR_PAGE.408.type')}
          </Text>
          <Heading
            css={{
              marginBottom: '4px',
              '@media (max-width: 464px)': {
                marginBottom: '16px',
              },
            }}
            level={1}
            size="lg"
          >
            {t('ERROR_PAGE.408.heading')}
          </Heading>
          <Heading
            css={{
              marginBottom: '12px',
              '@media (max-width: 464px)': {
                marginBottom: '19px',
              },
            }}
            level={6}
            size="xs"
          >
            {t('ERROR_PAGE.408.subHeading')}
          </Heading>
          <Text
            css={{
              'abyss-text-root': {
                marginBottom: '30px',
                '@media (max-width: 464px)': {
                  marginBottom: '9px',
                  maxWidth: '190px',
                },
              },
            }}
            fontSize="sm"
          >
            {t('ERROR_PAGE.408.body1')}
          </Text>
          <Layout.Group
            css={{
              cursor: 'pointer',
            }}
            data-auto-testid="refresh"
            data-testid="refresh"
            onClick={refreshPage}
            role="button"
          >
            <Text color="$refreshIconColor" fontSize="sm" fontWeight="bold">
              {t('ERROR_PAGE.408.Refresh')}
            </Text>
            <IconSymbol color="$refreshIconColor" icon="refresh" size="12px" />
          </Layout.Group>
        </PageContent>
      </PageContainer>
      {!isGuest && <StillNeedHelp />}
    </Fragment>
  );
};
